/*
 * clic-filter-set.js
 * ------------------
 * Behavior for the quiz-style PLP filter menu rendered by
 * snippets/clic-filter-set.liquid (spec: docs/PLP_FILTERS_SPEC.md §5).
 *
 * Owns: group open/close, "Show more", single-select groups, link-group
 * navigation that carries the active filter.* params, and the mobile apply
 * bar (defers the vendor's instant-apply while the drawer is open).
 *
 * Does NOT own filtering itself — inputs keep the vendor contract
 * (input[name="filter.*"] inside [data-collection-filters-form]) and
 * assets/filters.js (<collection-filters>) still fetches + swaps the section.
 * Group toggles deliberately avoid data-collapsible-trigger so filters.js'
 * first-four-open reconciliation never overrides the per-group `collapsed`.
 * The element is re-created on every AJAX render (it lives inside
 * [data-collection-filters]), so all state is derived from the DOM.
 */
(function () {
  'use strict';
  if (customElements.get('clic-filter-set')) return;

  const MOBILE_MAX = 767;
  const isMobile = () => window.innerWidth <= MOBILE_MAX;

  customElements.define(
    'clic-filter-set',
    class ClicFilterSet extends HTMLElement {
      connectedCallback() {
        this.form = this.closest('form');
        this.host = this.closest('collection-filters');
        // The section root (filters.js toggles `is-loading` on it). Never put
        // data-section-id on this element itself — closest() would match self.
        this.container = this.closest('[data-section-id]');
        this.sectionId = this.dataset.clicSection || (this.container && this.container.dataset.sectionId) || '';
        this.applyBar = this.querySelector('[data-clic-apply]');
        this.applyBtn = this.querySelector('[data-clic-apply-btn]');
        this.applyText = this.querySelector('[data-clic-apply-text]');
        this.applyLabel = this.dataset.applyLabel || 'Show {{count}} frames';
        this.useApplyBar = this.dataset.applyBar === 'true' && Boolean(this.applyBar);
        this.passThrough = false;
        this.dirty = false;
        this.countTimer = null;
        this.countAbort = null;

        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onFormInputCapture = this.onFormInputCapture.bind(this);
        this.onResize = this.onResize.bind(this);

        this.addEventListener('click', this.onClick);
        this.addEventListener('change', this.onChange);
        if (this.form && this.useApplyBar) {
          // Capture phase + stopImmediatePropagation: fires before the vendor's
          // bubbling `input` listener on the same form (also for events whose
          // target IS the form, e.g. the price slider re-dispatch).
          this.form.addEventListener('input', this.onFormInputCapture, true);
          window.addEventListener('resize', this.onResize);
          this.applyBtn && this.applyBtn.addEventListener('click', () => this.apply());
          if (isMobile()) this.classList.add('has-apply-bar');
          // Re-parent the bar to the drawer root: the vendor's AOS fade leaves a
          // `transform` on the <form>, which would make position:fixed resolve
          // against the (viewport-taller) form and push the bar offscreen. The
          // drawer root is the fixed, full-height element, so it is the right
          // containing block. Removed again in disconnectedCallback.
          if (this.host && this.applyBar.parentElement !== this.host) this.host.appendChild(this.applyBar);
        }
      }

      disconnectedCallback() {
        if (this.applyBar && this.applyBar.parentElement && this.applyBar.parentElement !== this) this.applyBar.remove();
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('change', this.onChange);
        if (this.form) this.form.removeEventListener('input', this.onFormInputCapture, true);
        window.removeEventListener('resize', this.onResize);
        if (this.countAbort) this.countAbort.abort();
      }

      /* ---------- group toggles / show more / link carry ---------- */

      onClick(event) {
        const toggle = event.target.closest('[data-clic-toggle]');
        if (toggle && this.contains(toggle)) {
          event.preventDefault();
          const panel = document.getElementById(toggle.getAttribute('aria-controls'));
          const open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(open));
          toggle.classList.toggle('is-expanded', open);
          if (panel) panel.hidden = !open;
          return;
        }

        const more = event.target.closest('[data-clic-more-toggle]');
        if (more && this.contains(more)) {
          event.preventDefault();
          const panel = document.getElementById(more.getAttribute('aria-controls'));
          const open = more.getAttribute('aria-expanded') !== 'true';
          more.setAttribute('aria-expanded', String(open));
          if (panel) {
            panel.querySelectorAll('[data-clic-more]').forEach((tile) => {
              tile.hidden = !open;
            });
            const tiles = panel.querySelector('[data-clic-tiles]');
            if (tiles) tiles.toggleAttribute('data-clic-more-open', open);
          }
          return;
        }

        const link = event.target.closest('[data-clic-link]');
        if (link && this.contains(link)) {
          const group = link.closest('[data-clic-carry]');
          if (!group || group.dataset.clicCarry !== 'true') return;
          const current = new URLSearchParams(window.location.search);
          const target = new URL(link.href, window.location.origin);
          current.forEach((value, key) => {
            if (key.indexOf('filter.') === 0 || key === 'sort_by') target.searchParams.append(key, value);
          });
          link.href = target.toString();
          // let the browser navigate
        }
      }

      onChange(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox' || !input.checked) return;
        const tiles = input.closest('[data-clic-tiles]');
        if (!tiles || tiles.dataset.clicSelect !== 'single') return;
        tiles.querySelectorAll('input[type="checkbox"]').forEach((other) => {
          if (other !== input && other.checked && !other.closest('[data-bucket-members]')) other.checked = false;
        });
      }

      /* ---------- mobile apply bar ---------- */

      onResize() {
        if (!this.useApplyBar) return;
        if (!isMobile()) {
          this.classList.remove('has-apply-bar');
          if (this.dirty) this.apply();
        } else {
          this.classList.add('has-apply-bar');
        }
      }

      drawerOpen() {
        const filters = this.host || this.closest('[data-collection-filters]');
        return Boolean(filters && filters.classList.contains('collection__filters--visible'));
      }

      onFormInputCapture(event) {
        if (this.passThrough || !isMobile()) return;
        // Only defer changes that come from the set itself (or the sort
        // inputs living in the same drawer form); anything else passes.
        if (!this.contains(event.target) && event.target !== this.form && !event.target.closest('[data-input-sort]')) return;
        if (!this.drawerOpen()) return;
        event.stopImmediatePropagation();
        this.dirty = true;
        this.showApplyBar();
        this.scheduleCount();
      }

      showApplyBar() {
        if (!this.applyBar) return;
        this.applyBar.hidden = false;
        requestAnimationFrame(() => this.applyBar.classList.add('is-visible'));
      }

      setCount(count) {
        if (!this.applyText) return;
        const label = this.applyLabel.replace('{{count}}', count == null ? '' : String(count)).replace(/\s{2,}/g, ' ').trim();
        this.applyText.textContent = label;
      }

      scheduleCount() {
        clearTimeout(this.countTimer);
        this.setCount(null);
        this.applyBtn && this.applyBtn.setAttribute('aria-busy', 'true');
        this.countTimer = setTimeout(() => this.fetchCount(), 350);
      }

      fetchCount() {
        if (!this.form || !this.sectionId) return;
        if (this.countAbort) this.countAbort.abort();
        this.countAbort = new AbortController();
        const params = new URLSearchParams(new FormData(this.form));
        const url = `${window.location.pathname}?section_id=${this.sectionId}&${params.toString()}`;
        fetch(url, { signal: this.countAbort.signal })
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const node = doc.querySelector('[data-products-count]');
            const match = node && node.textContent.replace(/,/g, '').match(/\d+/);
            this.setCount(match ? match[0] : null);
          })
          .catch(() => this.setCount(null))
          .finally(() => this.applyBtn && this.applyBtn.removeAttribute('aria-busy'));
      }

      apply() {
        if (!this.form) return;
        this.dirty = false;
        if (this.applyBar) this.applyBar.classList.remove('is-visible');
        this.passThrough = true;
        // Re-dispatch so the vendor's debounced submit (filters.js) runs once.
        this.form.dispatchEvent(new Event('input', { bubbles: true }));
        this.passThrough = false;
        // Close the drawer once the section has re-rendered (the vendor keeps
        // it open after an instant-apply; with a deferred apply the user
        // expects to land on the results). filters.js toggles `is-loading` on
        // the section container: wait for it to appear, then clear, then hide
        // from a FRESH TIMER — never from requestAnimationFrame.
        //
        // Why a timer (mobile scroll-freeze fix, 2026-09-24): after the render
        // the vendor re-adds the visible class and dispatches
        // `theme:scroll:lock`; theme.js applies that lock inside a
        // setTimeout(0) queued at render time, and that same timer CANCELS any
        // pending `theme:scroll:unlock`. On a slow phone the thread is busy
        // with the grid render, so our poll and a rAF could both run before
        // the vendor's timer: hideFiltersDrawer() dispatched the unlock, the
        // vendor's timer then cancelled it and locked the page — drawer
        // closed, page frozen. A timer queued from our poll is always ordered
        // after the vendor's (queued earlier, due earlier), so the unlock
        // runs last. ensureUnlocked() is the belt-and-braces for any other
        // ordering.
        const container = this.container;
        const host = this.host;
        if (!container || !host || typeof host.hideFiltersDrawer !== 'function') return;
        const started = Date.now();
        let sawLoading = false;
        const tick = () => {
          const loading = container.classList.contains('is-loading');
          if (loading) sawLoading = true;
          if (sawLoading && !loading) {
            setTimeout(() => {
              if (container.classList.contains('is-loading')) {
                setTimeout(tick, 100);
                return;
              }
              host.hideFiltersDrawer();
              this.ensureUnlocked();
            }, 50);
            return;
          }
          if (Date.now() - started < 10000) setTimeout(tick, 100);
        };
        setTimeout(tick, 100);
      }

      // After the drawer closes, the vendor's unlock is itself delayed 300ms
      // (`theme:scroll:unlock` detail). Check after that: if the page is still
      // scroll-locked (theme.js stamps `data-scroll-locked` on <html>) with the
      // drawer closed and no other theme overlay open, unlock it ourselves.
      // The theme's unlock handler re-checks the overlay classes, so this can
      // never unlock a cart drawer / modal / search that opened meanwhile.
      ensureUnlocked() {
        setTimeout(() => {
          if (this.drawerOpen()) return;
          if (!document.documentElement.hasAttribute('data-scroll-locked')) return;
          const overlays = ['js-drawer-open', 'js-drawer-open-cart', 'search-opened', 'modal--is-visible', 'js-quick-view-visible'];
          if (overlays.some((cls) => document.body.classList.contains(cls))) return;
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
        }, 450);
      }
    }
  );
})();
